import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@wundo/client/api.ts';
import type {
  OrganisationFeature,
  OrganisationFeatures,
} from '@wundo/shared/organisationFeature.ts';
import {
  ORGANISATION_FEATURE_DEFINITIONS,
  createOrganisation,
  getOrganisation,
  updateOrganisation,
  type OrganisationDetail,
  type OrganisationInput,
  type OrganisationType,
} from '@/lib/organisations.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { useSavedFlash } from '@/lib/useSavedFlash.ts';
import { getChangedFields } from '@/utils/getChangedFields.ts';

const KNOWN_FEATURES = new Set<string>(
  ORGANISATION_FEATURE_DEFINITIONS.map((feature) => feature.key),
);

/*
 * A new customer starts on the web app and its meetings. Only new ones: the
 * shared defaults stay off, since changing those would switch every existing
 * organisation that never set these two.
 */
const NEW_ORGANISATION_FEATURES: OrganisationFeatures = { webapp: true, meetings: true };

/*
 * The fields the organisation form owns, in the order the create request sends
 * them. It is a shape of its own rather than the organisation record: the record
 * also carries an id and its timestamps, and none of those are the form's to send.
 */
export interface OrganisationFormState {
  name: string;
  type: OrganisationType;
  msteams_tenant_id: string;
  default_cohort_id: string | null;
  internal_domains: string[];
  /* Null rather than empty, so clearing the box clears the domain. */
  primary_domain: string | null;
  /* Null rather than empty, so clearing the box takes the proxy away. */
  calendar_proxy_url: string | null;
  features: OrganisationFeatures;
}

const BLANK_ORGANISATION: OrganisationFormState = {
  name: '',
  type: 'business',
  msteams_tenant_id: '',
  default_cohort_id: null,
  internal_domains: [],
  primary_domain: null,
  calendar_proxy_url: null,
  features: NEW_ORGANISATION_FEATURES,
};

/** What a domain lookup filled in before the create form opens. */
export type OrganisationPrefill = Partial<
  Pick<
    OrganisationFormState,
    'name' | 'msteams_tenant_id' | 'internal_domains' | 'primary_domain'
  >
>;

function formStateFrom(organisation: OrganisationDetail): OrganisationFormState {
  return {
    name: organisation.name,
    type: organisation.type,
    msteams_tenant_id: organisation.msteams_tenant_id ?? '',
    default_cohort_id: organisation.default_cohort_id ?? null,
    internal_domains: organisation.internal_domains ?? [],
    primary_domain: organisation.primary_domain ?? null,
    calendar_proxy_url: organisation.calendar_proxy_url ?? null,
    /* Renamed features linger in the stored record; they are not ours to send back. */
    features: Object.fromEntries(
      Object.entries(organisation.features ?? {}).filter(([key]) =>
        KNOWN_FEATURES.has(key),
      ),
    ),
  };
}

/**
 * The organisation form's state, from loading the record to saving it. An edit
 * sends only what changed, diffed against what the last save returned: the page
 * stays open after a save.
 */
export function useOrganisationForm(
  organisationId: string | undefined,
  prefill: OrganisationPrefill | undefined,
) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const isEditing = organisationId !== undefined;

  const organisationQuery = useQuery({
    queryKey: ['organisation', organisationId],
    queryFn: () => getOrganisation(organisationId as string),
    enabled: isEditing,
  });

  const [form, setForm] = useState<OrganisationFormState>(() =>
    isEditing ? BLANK_ORGANISATION : { ...BLANK_ORGANISATION, ...prefill },
  );
  const [baseline, setBaseline] = useState<OrganisationFormState | null>(null);
  /* Three seconds, then the page stops congratulating itself. */
  const savedFlash = useSavedFlash(3000);
  const [error, setError] = useState('');

  /*
   * Filling the form from the record it edits, during render rather than in an
   * effect: an effect would paint the empty form first and then replace it.
   * React Query keeps the same object while the record is unchanged, so this
   * runs once per version of the organisation and never on a plain refetch.
   */
  const loaded = organisationQuery.data;
  const [filledFrom, setFilledFrom] = useState<OrganisationDetail | null>(null);
  if (loaded !== undefined && loaded !== filledFrom) {
    const next = formStateFrom(loaded);
    setFilledFrom(loaded);
    setForm(next);
    setBaseline(next);
  }

  const save = useMutation({
    mutationFn: async (): Promise<OrganisationDetail | null> => {
      if (organisationId === undefined) {
        return createOrganisation(form as unknown as OrganisationInput);
      }

      const changed = getChangedFields(
        (baseline ?? form) as unknown as Record<string, unknown>,
        form as unknown as Record<string, unknown>,
      );

      /* Nothing to send is still a successful save — there is nothing to save. */
      if (Object.keys(changed).length === 0) return null;

      return updateOrganisation(organisationId, changed as OrganisationInput);
    },
    onSuccess: (result) => {
      setError('');

      /* Staying on a create form that was saved invites a second, duplicate save. */
      if (!isEditing && result) {
        void queryClient.invalidateQueries({ queryKey: ['organisations'] });
        void router.navigate({ to: '/organisations/$id', params: { id: result.id } });
        return;
      }

      savedFlash.trigger();

      if (result) {
        const next = formStateFrom(result);
        setForm(next);
        setBaseline(next);
        void queryClient.invalidateQueries({ queryKey: ['organisations'] });
        void queryClient.invalidateQueries({ queryKey: ['organisation', result.id] });
      }
    },
    onError: (caught: unknown) => {
      savedFlash.hide();
      setError(
        apiErrorMessage(
          caught,
          caught instanceof ApiError && caught.status === 422
            ? 'Some of these details were refused. Check the fields and try again.'
            : 'Failed to save organisation',
        ),
      );
    },
  });

  function update(patch: Partial<OrganisationFormState>): void {
    setForm((current) => ({ ...current, ...patch }));
  }

  function setFeature(key: OrganisationFeature, value: boolean): void {
    setForm((current) => ({
      ...current,
      features: { ...current.features, [key]: value },
    }));
  }

  return {
    isPending: organisationQuery.isPending,
    isError: organisationQuery.isError,
    loaded,
    form,
    update,
    setFeature,
    save,
    saved: savedFlash.showing,
    error,
  };
}
